import React, { useMemo } from 'react';
import Link from 'next/link';
import { SearchResult } from '@/modules/Search/types';
import { cx } from 'class-variance-authority';

import SvgAltSearch from '@/components/common/Icons/AltSearch';
import SvgComputer from '@/components/common/Icons/Computer';
import SvgNoResults from '@/components/common/Icons/IconNoResults';
import { ContentSearchType } from '@/components/common/Providers/GraphqlProvider/generated';

import { extractTextFromLexicalOrHtml, highlightText, SEARCH_CATEGORIES } from '../../../utils';

const Pill = ({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cx(
      'cursor-pointer rounded-full border border-concrete px-3 py-1 text-xs font-medium transition-colors hover:border-blueLight hover:bg-blueLight/50',
      active ? 'border-blueLight bg-blueLight text-blueDark' : 'border-gray-200 text-dimGray'
    )}
  >
    {label}
  </button>
);

const ResultItem = ({
  query,
  result,
  onClick,
}: {
  query: string;
  result: SearchResult;
  onClick: (result: SearchResult) => void;
}) => {
  const description = useMemo(() => {
    return extractTextFromLexicalOrHtml(result.description || '');
  }, [result.description]);

  return (
    <div
      onClick={() => onClick(result)}
      className="group flex cursor-pointer items-start gap-4 border-b border-gray-500 px-2 py-2 transition-colors last:border-b-0 hover:bg-gray-50"
    >
      <div className="mt-1 text-gray-400 transition-colors group-hover:text-blueDark">
        {result.category.action === 'NAVIGATE' ? (
          <SvgComputer width={24} height={24} />
        ) : (
          <SvgAltSearch width={24} height={24} className="text-dimGray" />
        )}
      </div>
      <div className="flex-1">
        <h4 className="font-workSans text-sm font-semibold text-tundora group-hover:text-blueDark">
          {highlightText(result.title, query)}
        </h4>
        <p className="line-clamp-1 font-workSans text-xs text-dimGray">
          {highlightText(description, query)}
        </p>
        <p className="line-clamp-1 font-workSans text-xs text-dimGray">
          Proviene de <span className="font-bold">{result.category.label}</span>
        </p>
      </div>
    </div>
  );
};

const NoResults = () => (
  <div className="flex flex-col items-center justify-center p-12 text-center">
    <div className="mb-4 text-gray-300">
      <SvgNoResults width={64} height={64} />
    </div>
    <h4 className="font-workSans text-lg font-semibold text-[#4B4B4B]">
      No se encontraron resultados
    </h4>
    <p className="mt-2 max-w-xs text-sm text-[#808080]">
      Intenta con otros términos o ajusta tus filtros para encontrar lo que buscas.
    </p>
  </div>
);

export default function SearchResults({
  results,
  categories,
  query,
  onResultClick,
  onCategoryToggle,
  otherQueryParams = {},
}: {
  results: SearchResult[];
  categories: ContentSearchType[];
  query: string;
  onResultClick: (result: SearchResult) => void;
  onCategoryToggle: (categoryId: ContentSearchType) => void;
  otherQueryParams?: Record<string, any>;
}) {
  const hasResults = results.length > 0;

  const resultsByCategory = results.reduce(
    (acc, result) => {
      if (!acc[result.category.label]) {
        acc[result.category.label] = [];
      }
      acc[result.category.label].push(result);
      return acc;
    },
    {} as Record<string, SearchResult[]>
  );

  return (
    <div
      style={{ zIndex: 'var(--z-overlay)' }}
      className="w-full max-w-3xl overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl"
    >
      <div className="flex flex-wrap gap-2 border-b border-gray-100 p-4">
        {SEARCH_CATEGORIES.map((cat) => (
          <Pill
            key={cat.label}
            label={cat.label}
            active={!!categories.find((c) => c === cat.id)}
            onClick={() => onCategoryToggle(cat.id)}
          />
        ))}
      </div>

      <div className="max-h-[500px] overflow-y-auto bg-white">
        {!hasResults ? (
          <NoResults />
        ) : (
          Object.entries(resultsByCategory).map(([category, items]) => {
            return (
              <div key={category} className="mb-2 px-3">
                <div className="py-2">
                  <span className="text-xs font-semibold tracking-wider text-gray-400">
                    {category}
                  </span>
                </div>
                <div className="flex flex-col">
                  {items.map((item) => (
                    <ResultItem key={item.id} query={query} result={item} onClick={onResultClick} />
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="border-t border-gray-100 bg-white p-2 text-center">
        <Link
          href={{
            pathname: '/buscar',
            query: {
              ...otherQueryParams,
              query,
              category: categories,
            },
          }}
          className="font-workSans text-sm font-medium text-gray-400 transition-colors hover:text-blueDark"
        >
          Ver resultados avanzados
        </Link>
      </div>
    </div>
  );
}
